import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { FormSelect } from 'components/molecules/FormSelect';
import { useForm } from 'react-hook-form';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { FormInput } from 'components/molecules/FormInput';
import { Typography } from '@mui/material';
import { ESearchFilterKeys } from 'constants/SearchFilterTypes';
import { useLocation } from 'react-router-dom';
import { replaceUrlIfOnPage } from 'utils/history';
import { prepareFilters } from 'helpers/filters';
import { useTranslations } from 'hooks/useTranslations';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { useLang } from 'models/langContext';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import {
  filterHostTypeByAccessoryType,
  getFieldsByAccessoriesType,
} from 'pages/AncillariesPage/helpers';
import { EArtAccessoryType, EArtAncillaryType } from 'constants/ArtAccessoryType';
import { objectToQueryString } from 'helpers/searchQuery';
import { EPerPages } from 'models/table';
import { StyledAdvancedSearch, StyledSubtitle } from './styles';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';
import { FormAsyncMultiAutocomplete } from 'components/molecules/FormAsyncMultiAutocomplete';

export interface ISearchFormValues {
  accessoryTypeUniqueId: string;
  manufacturersUniqueIds: string[];
  productName: string;
  hostTypesUniqueIds: string[];
  calibresUniqueIds: string[];
  magnificationsUniqueIds: string[];
  utilitiesUniqueIds: string[];
  reticlesUniqueIds: string[];
  lightColoursUniqueIds: string[];
  compositionsUniqueIds: string[];
  capacity: string;
  proofHousesUniqueIds: string[];

  [key: string]: any;
}

export enum ESearchFormLabels {
  accessoryTypeUniqueId = 'ancillariesReferenceTable.accordion.accessoryType.label',
  manufacturersUniqueIds = 'ancillariesReferenceTable.accordion.accessoryManufacturer.label',
  productName = 'ancillariesReferenceTable.accordion.productName.label',
  hostTypesUniqueIds = 'ancillariesReferenceTable.accordion.hostType.label',
  calibresUniqueIds = 'ancillariesReferenceTable.accordion.calibre.label',
  magnificationsUniqueIds = 'ancillariesReferenceTable.accordion.magnification.label',
  utilitiesUniqueIds = 'ancillariesReferenceTable.accordion.utility.label',
  reticlesUniqueIds = 'ancillariesReferenceTable.accordion.reticle.label',
  lightColoursUniqueIds = 'ancillariesReferenceTable.accordion.lightColour.label',
  compositionsUniqueIds = 'ancillariesReferenceTable.accordion.composition.label',
  capacity = 'ancillariesReferenceTable.accordion.capacity.label',
  proofHousesUniqueIds = 'ancillariesReferenceTable.accordion.proofMark.label',
}

export const initialFormValues: ISearchFormValues = {
  accessoryTypeUniqueId: '',
  manufacturersUniqueIds: [],
  productName: '',
  hostTypesUniqueIds: [],
  calibresUniqueIds: [],
  magnificationsUniqueIds: [],
  utilitiesUniqueIds: [],
  reticlesUniqueIds: [],
  lightColoursUniqueIds: [],
  compositionsUniqueIds: [],
  capacity: '',
  proofHousesUniqueIds: [],
};

interface ISearchForm {
  onSubmit: (arg: any) => void;
  initialValues: ISearchFormValues;
  isTransactionPage?: boolean;
  type?: string | null;
  setIsQueryAncillariesReferenceLoading: (arg: any) => void;
  isSearchButtonDisabled?: boolean;
  currentPage?: number;
  perPage?: EPerPages;
}

const AncillariesReferenceAccesoriessSearchForm = ({
  onSubmit,
  initialValues,
  isTransactionPage,
  type = null,
  isSearchButtonDisabled = false,
  setIsQueryAncillariesReferenceLoading,
  perPage,
  currentPage,
}: ISearchForm) => {
  const [values, setValues] = useState<ISearchFormValues>(initialValues);
  const [accessFields, setAccessFields] = useState<string[]>([]);
  const [accessoryManufacturer, setAccessoryManufacturer] = useState<IDictionary[] | null>(
    null,
  );
  const [accessoryHostType, setAccessoryHostType] = useState<IDictionary[] | null>(null);
  const [isLoadingDictionaries, setIsLoadingDictionaries] = useState<boolean>(false);
  const [isInitialFormLoad, setIsInitialFormLoad] = useState(true);
  const { state: routerState, pathname }: any = useLocation();
  const { t } = useTranslations();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<ISearchFormValues>({
    defaultValues: initialValues,
  });

  const { selectedLanguage } = useLang();

  const {
    loadDictionaries,
    artAccessoryCalibre,
    artAccessoryComposition,
    artAccessoryHostType,
    artAccessoryLightColour,
    artAccessoryMagnification,
    artAccessoryReticle,
    artAccessoryType,
    artAccessoryUtility,
    artAccessoryProofHouse,
    artAccessoryManufacturerFlashEliminator,
    artAccessoryManufacturerLaserLightModule,
    artAccessoryManufacturerMagazine,
    artAccessoryManufacturerOpticalSight,
    artAccessoryManufacturerSuppressor,
  } = useAncillaries();

  const submitForm = (data: ISearchFormValues) => {
    if (data.invokedByPageChange !== true) {
      setIsQueryAncillariesReferenceLoading(true);
    }
    data.invokedByPageChange = undefined;

    if (isTransactionPage) {
      const query = objectToQueryString(data);
      if (query.length) {
        replaceUrlIfOnPage(
          `${pathname}?type=${type}&expand=ancillaries&ancillaryOptionName=${EArtAncillaryType.ACCESSORIES}&${query}`,
        );
      } else {
        replaceUrlIfOnPage(
          `${pathname}?type=${type}&expand=ancillaries&ancillaryOptionName=${EArtAncillaryType.ACCESSORIES}`,
        );
      }
    } else {
      const query = objectToQueryString({
        ...data,
        perPage,
        currentPage,
      });
      if (query.length) {
        replaceUrlIfOnPage(
          `${pathname}?expand=ancillaries-reference-table&ancillaryOptionName=${EArtAncillaryType.ACCESSORIES}&${query}`,
        );
      } else {
        replaceUrlIfOnPage(
          `${pathname}?expand=ancillaries-reference-table&ancillaryOptionName=${EArtAncillaryType.ACCESSORIES}`,
        );
      }
    }
    onSubmit(data);
  };

  useEffect(() => {
    if (!isInitialFormLoad) {
      handleSubmit((val) => submitForm({ ...val, invokedByPageChange: true }))();
    } else {
      setIsInitialFormLoad(false);
    }
  }, [perPage, currentPage]);

  const setLegalEntity = () => {
    const formValues = {
      ...initialValues,
      ...routerState?.formValues,
    };

    setValues(formValues);
    reset(formValues);
  };

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as ISearchFormValues);
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  useEffect(() => {
    if (!isLoadingDictionaries) {
      setValues(initialValues);
      reset(initialValues);
      handleSubmit(onSubmit)();
    }
  }, [initialValues, isLoadingDictionaries]);

  useEffect(() => {
    (async function initDictionaries() {
      setIsLoadingDictionaries(true);
      await loadDictionaries([
        EDictionaryTypes.ArtAccessoryType,
        EDictionaryTypes.ArtAccessoryCalibre,
        EDictionaryTypes.ArtAccessoryHostType,
        EDictionaryTypes.ArtaccessoryComposition,
        EDictionaryTypes.ArtAccessoryMagnification,
        EDictionaryTypes.ArtAccessoryUtility,
        EDictionaryTypes.ArtAccessoryReticle,
        EDictionaryTypes.ArtAccessoryLightColour,
        EDictionaryTypes.ArtAccessoryProofHouse,
        EDictionaryTypes.ArtAccessoryManufacturerFlashEliminator,
        EDictionaryTypes.ArtAccessoryManufacturerLaserLightModule,
        EDictionaryTypes.ArtAccessoryManufacturerMagazine,
        EDictionaryTypes.ArtAccessoryManufacturerOpticalSight,
        EDictionaryTypes.ArtAccessoryManufacturerSuppressor,
      ]);
      setIsLoadingDictionaries(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async function init() {
      if (routerState?.formValues) {
        const formValues = {
          ...initialValues,
          ...routerState.formValues,
          legalityUniqueId: routerState?.legalityUniqueId,
        };
        setValues(formValues);
        reset(formValues);
      }
      if (routerState?.legalEntity) {
        setLegalEntity();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setAccessoryManufacturerDictionaryData = (accessoriesTypeId: EArtAccessoryType) => {
    switch (accessoriesTypeId) {
      case EArtAccessoryType.MAGAZINES:
        setAccessoryManufacturer(artAccessoryManufacturerMagazine);
        break;
      case EArtAccessoryType.LASER_LIGHT_MODULES:
        setAccessoryManufacturer(artAccessoryManufacturerLaserLightModule);
        break;
      case EArtAccessoryType.OPTICAL_SIGHTS:
        setAccessoryManufacturer(artAccessoryManufacturerOpticalSight);
        break;
      case EArtAccessoryType.FLASH_ELIMINATORS:
        setAccessoryManufacturer(artAccessoryManufacturerFlashEliminator);
        break;
      case EArtAccessoryType.SUPPRESSORS:
        setAccessoryManufacturer(artAccessoryManufacturerSuppressor);
        break;
      default:
        setAccessoryManufacturer(null);
        break;
    }
  };

  const accessoryTypeChange = (value: any) => {
    reset({ ...initialValues, accessoryTypeUniqueId: value });
    if (value) {
      const fields = getFieldsByAccessoriesType(value);
      setAccessFields(fields);
      setAccessoryManufacturerDictionaryData(value);
      setAccessoryHostType(
        filterHostTypeByAccessoryType(value as EArtAccessoryType, artAccessoryHostType),
      );
    } else {
      setAccessFields([]);
      setAccessoryManufacturer(null);
      setAccessoryHostType(null);
    }
  };

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name && name !== 'accessoryTypeUniqueId') {
      reset({ ...getValues(), [name]: '' });
    } else {
      reset(initialValues);
      accessoryTypeChange('');
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  useEffect(() => {
    if (initialValues.accessoryTypeUniqueId) {
      accessoryTypeChange(initialValues.accessoryTypeUniqueId);
    }
  }, [initialValues]);

  const checkDisabled = (fieldName: string) => !accessFields.includes(fieldName);

  return (
    <SearchWrapper>
      <form onSubmit={handleSubmit(submitForm)}>
        <StyledAdvancedSearch container columnSpacing={2} columns={10}>
          <Grid size={{ xs: 12 }}>
            <StyledSubtitle size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
              <Typography variant="subtitle2">
                {t('ancillaries.accordion.subtilte.label')}
              </Typography>
            </StyledSubtitle>
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(artAccessoryType)}
              name="accessoryTypeUniqueId"
              label={t(ESearchFormLabels.accessoryTypeUniqueId)}
              setValue={(name: string, value: string) => {
                setValue(name, value);
                accessoryTypeChange(value);
              }}
              canBeEmpty
              control={control}
              errors={errors}
              onSelect={accessoryTypeChange}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormMultiSelect
              name="manufacturersUniqueIds"
              options={mapDictionaryToOptionProp(accessoryManufacturer)}
              label={t(ESearchFormLabels.manufacturersUniqueIds)}
              control={control}
              errors={errors}
              disabled={checkDisabled('manufacturersUniqueIds')}
              setValue={setValue}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="productName"
              label={t(ESearchFormLabels.productName)}
              control={control}
              errors={errors}
              disabled={checkDisabled('productName')}
              setValue={setValue}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAccessoryCalibre}
              name="calibresUniqueIds"
              label={t(ESearchFormLabels.calibresUniqueIds)}
              control={control}
              errors={errors}
              disabled={checkDisabled('calibresUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAccessoryHostType}
              name="hostTypesUniqueIds"
              label={t(ESearchFormLabels.hostTypesUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('hostTypesUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtaccessoryComposition}
              name="compositionsUniqueIds"
              label={t(ESearchFormLabels.compositionsUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('compositionsUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAccessoryMagnification}
              name="magnificationsUniqueIds"
              label={t(ESearchFormLabels.magnificationsUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('magnificationsUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAccessoryUtility}
              name="utilitiesUniqueIds"
              label={t(ESearchFormLabels.utilitiesUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('utilitiesUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="capacity"
              label={t(ESearchFormLabels.capacity)}
              control={control}
              errors={errors}
              disabled={checkDisabled('capacity')}
              setValue={setValue}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAccessoryReticle}
              name="reticlesUniqueIds"
              label={t(ESearchFormLabels.reticlesUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('reticlesUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAccessoryLightColour}
              name="lightColoursUniqueIds"
              label={t(ESearchFormLabels.lightColoursUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('lightColoursUniqueIds')}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ArtAccessoryProofHouse}
              name="proofHousesUniqueIds"
              label={t(ESearchFormLabels.proofHousesUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
              disabled={checkDisabled('proofHousesUniqueIds')}
            />
          </Grid>
          <Grid
            size={{ xs: 12 }}
            container
            columns={10}
            columnSpacing={2}
            sx={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Grid size={{ xs: 10, sm: 10, md: 5, lg: 2 }}>
              <Button
                variant={EButtonVariants.contained}
                size={EButtonSizes.small}
                disabled={isSearchButtonDisabled}
                label={t('ancillaries.accordion.search.button')}
                type="submit"
                id="ancillary-reference-table-accessory-search"
                fullWidth
                sx={{
                  mt: {
                    xs: 2,
                    sm: 3.5,
                  },
                }}
              />
            </Grid>
          </Grid>
        </StyledAdvancedSearch>
      </form>
      <SelectedFilters
        name="ancillaries_reference_accesories"
        values={values}
        labels={ESearchFormLabels}
        handleDelete={handleResetField}
        type={ESearchFilterKeys.ancillaries}
        showSaveFilters={false}
        saveQueryParams={['type', 'ancillaryOptionName']}
        dictionaries={{
          accessoryTypeUniqueId: prepareFilters(artAccessoryType, 'uniqueId', 'name'),
          manufacturersUniqueIds: prepareFilters(accessoryManufacturer, 'uniqueId', 'name'),
          calibresUniqueIds: prepareFilters(artAccessoryCalibre, 'uniqueId', 'name'),
          hostTypesUniqueIds: prepareFilters(artAccessoryHostType, 'uniqueId', 'name'),
          compositionsUniqueIds: prepareFilters(artAccessoryComposition, 'uniqueId', 'name'),
          magnificationsUniqueIds: prepareFilters(
            artAccessoryMagnification,
            'uniqueId',
            'name',
          ),
          utilitiesUniqueIds: prepareFilters(artAccessoryUtility, 'uniqueId', 'name'),
          reticlesUniqueIds: prepareFilters(artAccessoryReticle, 'uniqueId', 'name'),
          lightColoursUniqueIds: prepareFilters(artAccessoryLightColour, 'uniqueId', 'name'),
          proofHousesUniqueIds: prepareFilters(artAccessoryProofHouse, 'uniqueId', 'name'),
        }}
        saveRouterStateAfterClearAll={false}
      />
    </SearchWrapper>
  );
};

export { AncillariesReferenceAccesoriessSearchForm };
